// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_V1 } from '../Constant';
import { storeProfile } from '../Util';

// Create the AuthContext
const AuthContext = createContext(null);

// Custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};

// AuthProvider component to wrap your application
export const AuthProvider = ({ children }) => {
  const LAST_REFRESH_KEY = 'last-auth-refresh';

  // Optimization: Lazy initialize state to prevent "Loading..." flash on page reload
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const lastRefreshTimestamp = localStorage.getItem(LAST_REFRESH_KEY);
    return !!(lastRefreshTimestamp && Date.now() - parseInt(lastRefreshTimestamp, 10) < 55 * 60 * 1000);
  });

  const [userType, setUserType] = useState(() => {
    try {
      const profile = localStorage.getItem('profile');
      return profile ? (JSON.parse(profile).userType || 'normal') : 'normal';
    } catch {
      return 'normal';
    }
  });

  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(
    () => localStorage.getItem('subscription-expired') === '1'
  );

  const [loadingAuth, setLoadingAuth] = useState(() => {
    const lastRefreshTimestamp = localStorage.getItem(LAST_REFRESH_KEY);
    return !(lastRefreshTimestamp && Date.now() - parseInt(lastRefreshTimestamp, 10) < 55 * 60 * 1000);
  });

  const refreshPromise = useRef(null);
  const proactiveRefreshTimer = useRef(null);
  // Stable ref to refreshAccessToken so the proactive timer callback
  // always calls the latest version without stale closure issues.
  const refreshAccessTokenRef = useRef(null);

  // Helper function to reset authentication state
  const resetAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setLoadingAuth(false);
    setUserType('normal');
    setIsSubscriptionExpired(false);
    localStorage.removeItem(LAST_REFRESH_KEY);
    localStorage.removeItem('profile');
    localStorage.removeItem('subscription-expired');
  }, []);

  // Fetch profile from backend and persist in localStorage for use across the app
  const fetchAndStoreProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API_V1}/profile`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data) {
          storeProfile(data.data);
          setUserType(data.data.userType || 'normal');
        }
      }
    } catch {
      // Non-critical — profile will be fetched again on next auth cycle
    }
  }, []);

  const _scheduleProactiveRefresh = () => {
    if (proactiveRefreshTimer.current) clearTimeout(proactiveRefreshTimer.current);
    // Refresh 55 min after last successful auth — before the 1-hr accessToken expires.
    proactiveRefreshTimer.current = setTimeout(() => {
      refreshAccessTokenRef.current?.(true);
    }, 55 * 60 * 1000);
  };

  // Function to refresh the access token.
  // For web clients, this relies on the HttpOnly authCode cookie being sent automatically by the browser.
  const refreshAccessToken = useCallback(async (forceBypassCache = false) => {
    const lastRefreshTimestamp = localStorage.getItem(LAST_REFRESH_KEY);

    // If a refresh happened recently, we can trust the session is active.
    // We skip this check if forceBypassCache is true (used when a 401 occurs).
    const cachedSubscriptionExpired = localStorage.getItem('subscription-expired');
    if (!forceBypassCache && lastRefreshTimestamp && Date.now() - parseInt(lastRefreshTimestamp, 10) < 55 * 60 * 1000
        && cachedSubscriptionExpired !== null) {
      // Sync state and skip network call.
      // Only use cache when subscription-expired key is present — if absent (session predates
      // this feature, or login() ran without writing it), fall through to network for
      // an authoritative value.
      setIsAuthenticated(true);
      setIsSubscriptionExpired(cachedSubscriptionExpired === '1');
      setLoadingAuth(false);
      return true;
    }
    // Deduplication: If a refresh is already in progress, return the existing promise
    if (refreshPromise.current) {
      return refreshPromise.current;
    }

    setLoadingAuth(true);

    refreshPromise.current = (async () => {
      try {
        const response = await fetch(`${API_V1}/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
          // For web clients, the HttpOnly authCode cookie is automatically sent by the browser.
          // No 'body' is needed here for the authCode itself.
        });

        if (!response.ok) {
          // If the backend responds with a 401/403, it means the authCode is expired/invalid
          // or not present, and the backend has likely cleared the cookie.
          console.warn('Failed to refresh access token. AuthCode might be expired or invalid. Status:', response.status);
          resetAuthState();
          return false;
        }

        const data = await response.json();

        if (data.status === 'success') {
          setIsAuthenticated(true);
          const expired = data.subscriptionExpired ?? false;
          setIsSubscriptionExpired(expired);
          localStorage.setItem('subscription-expired', expired ? '1' : '0');
          localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
          fetchAndStoreProfile();
          _scheduleProactiveRefresh();
          return true;
        }

        resetAuthState();
        return false;
      } catch (error) {
        console.error('Error refreshing Access Token:', error);
        resetAuthState();
        return false;
      } finally {
        setLoadingAuth(false);
        // Clear the promise ref so the next call can make a new request
        refreshPromise.current = null;
      }
    })();

    return refreshPromise.current;
  }, [resetAuthState, fetchAndStoreProfile]);

  // Keep ref in sync so the proactive timer callback always has the latest function.
  refreshAccessTokenRef.current = refreshAccessToken;

  // Initial authentication check on component mount.
  // This attempts to refresh the token, relying on an existing HttpOnly authCode cookie.
  useEffect(() => {
    refreshAccessToken();
  }, [refreshAccessToken]);

  const login = useCallback(async () => {
    // Force a network refresh so subscriptionExpired + last-auth-refresh are both written
    // before PrivateRoute renders. Avoids the window where subscription-expired is absent
    // from localStorage and the cache path incorrectly treats it as not-expired.
    await refreshAccessToken(true);
  }, [refreshAccessToken]);

  // Function to handle logout. Sends a request to the backend to clear the
  // HttpOnly authCode cookie.

  const logout = useCallback(async () => {
    if (proactiveRefreshTimer.current) clearTimeout(proactiveRefreshTimer.current);
    resetAuthState(); // Clear frontend state immediately
    try {
      await fetch(`${API_V1}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // Even if backend logout fails, frontend state is already cleared.
    }
  }, [resetAuthState]);

  // Value provided by the context
  const authContextValue = {
    isAuthenticated,
    loadingAuth,
    userType,
    isSubscriptionExpired,
    login,
    logout,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};